/**
 * Frontend TypeScript types for Expense Tracker module
 *
 * Provides type definitions for expense reports, receipts, and related entities
 * used in the frontend views and services.
 */

/**
 * Expense report status values
 */
export type ExpenseReportStatus = 'DRAFT' | 'SUBMITTED' | 'PAID' | 'CANCELLED';

/**
 * Receipt extraction status
 */
export type ExtractionStatus = 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'MANUAL';

/**
 * Expense types for categorizing receipts (Wyden types)
 */
export type ExpenseType =
  | 'EDUCATION'
  | 'CATERING'
  | 'REPRESENTATION'
  | 'TRAVEL'
  | 'OTHER'
  | 'OFFICE_MATERIAL';

/**
 * Receipt image data for display
 */
export interface Receipt {
  id: string;
  expense_report_id: string;
  original_filename: string;
  stored_filename: string;
  mime_type: string;
  file_size: number;
  vendor: string | null;
  vendor_name?: string | null; // Keep for backward compatibility
  receipt_date: string | null;
  amount: number | null;
  currency: string | null;
  amount_chf: number | null;
  conversion_rate: number | null;
  conversion_date: string | null;
  vat_amount: number | null;
  vat_percent: number | null;
  vat_rate?: number | null; // Keep for backward compatibility
  ai_suggested_vat_rate?: number | null;
  expense_type: ExpenseType | null;
  notes: string | null;
  description?: string | null; // Keep for backward compatibility
  extraction_status: ExtractionStatus;
  extraction_confidence: number | null;
  raw_ocr_text: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Expense report summary for list view
 */
export interface ExpenseReportSummary {
  id: string;
  expenditure_request_id: string;
  user_id: string;
  status: ExpenseReportStatus;
  total_amount_chf: number | null;
  receipt_count: number;
  created_at: string;
  submitted_at: string | null;
  request_title: string;
  request_amount: number;
  request_currency: string;
  /** Submitter display name (for Department Head view) */
  submitter_name?: string;
  /** Submitter email (for Department Head view) */
  submitter_email?: string;
}

/**
 * Linked expenditure request info for detail view
 */
export interface LinkedExpenditureRequest {
  id: string;
  title: string;
  amount: number;
  currency: string;
  status: string;
  approved_at: string | null;
  approval_date?: string | null;
  requester_name?: string;
  approved_amount?: number;
}

/**
 * Submitter info for expense report detail
 */
export interface Submitter {
  id: string;
  display_name: string;
  email: string;
}

/**
 * Budget tracking info
 */
export interface BudgetInfo {
  approved: number;
  spent: number;
  remaining: number;
  currency: string;
  percentUsed: number;
}

/**
 * Payment profile for expense report
 */
export interface PaymentProfile {
  id: string;
  user_id: string;
  iban: string;
  swift_bic: string | null;
  account_holder_name: string;
  street_address: string | null;
  postal_code: string | null;
  city: string | null;
  country: string | null;
}

/**
 * Expense summary by type
 */
export interface ExpenseSummaryByType {
  type: string;
  code: number;
  amount_chf: number;
}

/**
 * Status history entry for tracking expense report status changes
 */
export interface ExpenseReportStatusHistoryEntry {
  status: ExpenseReportStatus;
  timestamp: string; // ISO 8601 timestamp
  userId: string; // User who made the change
  userName?: string; // Display name of the user (populated by API)
  notes?: string; // Optional notes about the change
}

/**
 * Expense report with full details including receipts
 */
export interface ExpenseReportDetail {
  id: string;
  expenditure_request_id: string;
  user_id: string;
  status: ExpenseReportStatus;
  total_amount_chf: number | null;
  digital_signature_confirmed: boolean;
  submitted_at: string | null;
  payment_initiated_at: string | null;
  paid_at: string | null;
  status_history: ExpenseReportStatusHistoryEntry[];
  created_at: string;
  updated_at: string;
  /** Unique reference number for the expense report */
  reference?: string;
  receipts: Receipt[];
  expenditure_request?: LinkedExpenditureRequest;
  // Enhanced fields from Team Assistant API
  submitter?: Submitter;
  linked_request?: LinkedExpenditureRequest;
  budget?: BudgetInfo;
  payment_profile?: PaymentProfile | null;
  summary_by_type?: ExpenseSummaryByType[];
  /** Whether the report is editable (based on status and permissions) */
  isEditable?: boolean;
  // QuickBooks sync fields
  quickbooks_bill_id?: string | null;
  quickbooks_synced_at?: string | null;
  quickbooks_sync_error?: string | null;
}

/**
 * Input for creating a new expense report
 */
export interface CreateExpenseReportInput {
  expenditureRequestId: string;
}

/**
 * Input for updating an expense report
 */
export interface UpdateExpenseReportInput {
  status?: ExpenseReportStatus;
  digital_signature_confirmed?: boolean;
}

/**
 * API response wrapper for expense report
 */
export interface ExpenseReportResponse {
  data: ExpenseReportDetail;
}

/**
 * API response wrapper for expense report list
 */
export interface ExpenseReportListResponse {
  data: ExpenseReportSummary[];
}

/**
 * API response wrapper for receipt
 */
export interface ReceiptResponse {
  data: Receipt;
  message?: string;
}

/**
 * Status style configuration for badges
 */
export interface StatusStyle {
  bg: string;
  text: string;
  icon: string;
}

/**
 * Status styles mapping
 */
export const EXPENSE_REPORT_STATUS_STYLES: Record<ExpenseReportStatus, StatusStyle> = {
  DRAFT: { bg: 'bg-muted', text: 'text-muted-foreground', icon: 'edit' },
  SUBMITTED: { bg: 'bg-blue-500/10', text: 'text-blue-500', icon: 'send' },
  PAID: { bg: 'bg-green-500/10', text: 'text-green-500', icon: 'checkCircle' },
  CANCELLED: { bg: 'bg-destructive/10', text: 'text-destructive', icon: 'xCircle' },
};

/**
 * Format status for display
 */
export function formatExpenseReportStatus(status: ExpenseReportStatus): string {
  const statusLabels: Record<ExpenseReportStatus, string> = {
    DRAFT: 'Draft',
    SUBMITTED: 'Submitted',
    PAID: 'Paid',
    CANCELLED: 'Cancelled',
  };
  return statusLabels[status] || status;
}

/**
 * Format currency amount with symbol
 */
export function formatCurrency(amount: number | null, currency: string = 'CHF'): string {
  if (amount === null) return '—';
  return new Intl.NumberFormat('de-CH', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Receipt form data for editing
 */
export interface ReceiptFormData {
  vendor: string | null;
  date: string | null;          // ISO 8601 (YYYY-MM-DD)
  amount: number | null;
  currency: string | null;      // ISO 4217
  vatPercent: number | null;    // 0, 2.6, or 8.1
  expenseType: string | null;   // Wyden category key
}

/**
 * Receipt form component options
 */
export interface ReceiptFormOptions {
  container: HTMLElement;
  receiptId: string;
  initialData: ReceiptFormData;
  confidenceScores?: {
    vendor: number;
    date: number;
    amount: number;
    currency: number;
    vatPercent: number;
    expenseType: number;
  };
  imageUrl: string;
  onSave: (data: ReceiptFormData) => Promise<void>;
  onCancel?: () => void;
}

/**
 * Wyden expense type definition
 */
export interface WydenExpenseType {
  value: string;
  label: string;
  code: number;
  description: string;
}

/**
 * Swiss VAT rate option
 */
export interface SwissVATRate {
  value: number;
  label: string;
}
